import { api } from "@/api";
import type {
  CreditPackageVo,
  PlanVo,
  UserPlanVo,
  UserProfile,
  UserProfileUpdateRequest,
} from "@/api/Api";
import { logger } from "@/common/logger";
import { action, computed, makeObservable, observable } from "mobx";

type SetKeys = keyof Pick<
  UserStore,
  | "loading"
  | "user"
  | "userCredits"
  | "creditsInfo"
  | "plansInfo"
  | "freeCreditAmount"
  | "userPlanList"
  | "isPricingModalOpen"
>;

export class UserStore {
  @observable user: UserProfile | null = null;
  @observable loading: boolean = true;
  @observable updateUserLoading: boolean = false;
  @observable userCredits: number = 0;
  @observable isPricingModalOpen: boolean = false;
  @observable creditsInfo: CreditPackageVo[] = [];
  @observable plansInfo: PlanVo[] = [];
  @observable freeCreditAmount: number = 0;
  @observable userPlanList: UserPlanVo[] = [];

  constructor() {
    // 使用装饰器时仍需在 MobX 6 调用 makeAutoObservable/makeObservable 以启用响应式
    makeObservable(this);
  }

  @computed get isLogin() {
    return !!(this.user?.id || this.user?.email);
  }

  @computed get sortedPlans() {
    const rankMap: Record<string, number> = {
      studio_plan_monthly: 3,
      studio_plan_yearly: 3,
      pro_plan_monthly: 2,
      pro_plan_yearly: 2,
      free_plan: 1,
    };

    return [...this.userPlanList].sort((a, b) => {
      const rankA = rankMap[a.planCode || ""] || 0;
      const rankB = rankMap[b.planCode || ""] || 0;

      if (rankB !== rankA) {
        return rankB - rankA;
      }

      // 同等级按过期时间排序（晚过期的在前？）或者随便排，用户说“同等级中按 expireTime 排序”，通常指晚过期的在前或者早过期的在前。
      // 一般来说展示生效中的最高等级，如果时间一样，可能差异不大。这里默认晚过期的在前。
      return (
        new Date(b.expireTime || 0).getTime() -
        new Date(a.expireTime || 0).getTime()
      );
    });
  }

  @computed get regularPlan() {
    return this.sortedPlans.find((p) => p.from !== "REDEMPTION_CODE") || null;
  }

  @computed get giftedPlan() {
    return this.sortedPlans.find((p) => p.from === "REDEMPTION_CODE") || null;
  }

  @computed get userPlan() {
    return this.sortedPlans[0] || null;
  }

  @computed get isDisplayPlanGifted() {
    return this.userPlan?.from === "REDEMPTION_CODE";
  }

  @computed get isFreeUser() {
    const code = this.userPlan?.planCode;

    return !code || code === "free_plan";
  }

  @action set<K extends SetKeys>(k: K, v: UserStore[K]) {
    this[k] = v as never;
  }

  @action setIsPricingModalOpen(v: boolean) {
    this.set("isPricingModalOpen", v);
  }

  @action async fetchUser(): Promise<UserProfile> {
    this.loading = true;

    try {
      const res = await api.user.currentUserProfile();
      this.set("user", res.data);

      if (res.data?.id) {
        logger.setUser(res.data.id.toString());

        await Promise.all([
          this.fetchUserPlan(),
          this.fetchUserCredits(),
        ]).catch((err) =>
          console.error("Failed to load user plan or credits:", err),
        );
      }

      return res.data;
    } finally {
      this.loading = false;
    }
  }

  @action async updateUser(data: UserProfileUpdateRequest) {
    if (this.updateUserLoading) {
      return;
    }

    this.updateUserLoading = true;

    try {
      await api.user.updateUserProfile(data);

      this.fetchUser();
    } finally {
      this.updateUserLoading = false;
    }
  }

  @action async fetchUserCredits() {
    const res = await api.credits.getUserCredit();
    this.set("userCredits", parseInt(res.data?.creditAmount || "0", 10));
  }

  @action async fetchUserPlan() {
    const res = await api.plans.queryUserPlanList();
    this.set("userPlanList", res.data || []);
  }
}

export const userStore = new UserStore();
